import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaPlay, FaPause } from 'react-icons/fa';
import styles from './fullscreen-gallery.module.scss';

const AUTOPLAY_INTERVAL = 3000; // ms

interface FullscreenGalleryProps {
  images: Array<{ id: number; fullUrl: string; name: string }>;
  initialIndex: number;
  onClose: () => void;
}

export const FullscreenGallery: React.FC<FullscreenGalleryProps> = ({
  images,
  initialIndex,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isAutoplay, setIsAutoplay] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const nextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const previousImage = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const scrollThumbnails = useCallback((direction: 'left' | 'right') => {
    if (thumbnailContainerRef.current) {
      const scrollAmount = 300;
      const currentScroll = thumbnailContainerRef.current.scrollLeft;
      thumbnailContainerRef.current.scrollTo({
        left: currentScroll + (direction === 'left' ? -scrollAmount : scrollAmount),
        behavior: 'smooth'
      });
    }
  }, []);

  // Autoplay progress bar logic
  useEffect(() => {
    if (!isAutoplay) {
      setProgress(0);
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    setProgress(0);
    progressRef.current = 0;
    const start = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - start + progressRef.current;
      const percent = Math.min((elapsed / AUTOPLAY_INTERVAL) * 100, 100);
      setProgress(percent);
      if (percent >= 100) {
        setProgress(0);
        progressRef.current = 0;
        nextImage();
      }
    }, 30);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAutoplay, currentIndex, nextImage]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
          nextImage();
          break;
        case 'ArrowLeft':
          previousImage();
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextImage, previousImage, onClose]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAutoplay) {
      interval = setInterval(nextImage, AUTOPLAY_INTERVAL);
    }
    return () => clearInterval(interval);
  }, [isAutoplay, nextImage]);

  useEffect(() => {
    // Scroll active thumbnail into view
    if (thumbnailContainerRef.current) {
      const activeThumbnail = thumbnailContainerRef.current.children[currentIndex] as HTMLElement;
      if (activeThumbnail) {
        const containerWidth = thumbnailContainerRef.current.offsetWidth;
        const thumbnailLeft = activeThumbnail.offsetLeft;
        const thumbnailWidth = activeThumbnail.offsetWidth;
        
        thumbnailContainerRef.current.scrollTo({
          left: thumbnailLeft - (containerWidth / 2) + (thumbnailWidth / 2),
          behavior: 'smooth'
        });
      }
    }
  }, [currentIndex]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  return (
    <div className={styles.galleryOverlay}>
      {isAutoplay && (
        <div className={styles.autoplayProgressBarWrapper}>
          <div
            className={styles.autoplayProgressBar}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      <button className={styles.closeButton} onClick={onClose}>
        <FaTimes />
      </button>
      <button
        className={styles.autoplayButton}
        onClick={() => setIsAutoplay(!isAutoplay)}
      >
        {isAutoplay ? <FaPause /> : <FaPlay />}
      </button>
      <div className={styles.galleryMain}>
        <button className={styles.mainNavButton} onClick={previousImage}>
          <FaChevronLeft />
        </button>
        <div className={styles.imageContainer}>
          {isLoading && <div className={styles.loading}>Loading...</div>}
          <img
            src={images[currentIndex].fullUrl}
            alt={images[currentIndex].name}
            onLoad={handleImageLoad}
            className={styles.image}
          />
          <div className={styles.imageInfo}>
            <span>{images[currentIndex].name}</span>
            <span>{currentIndex + 1} / {images.length}</span>
          </div>
        </div>
        <button className={styles.mainNavButton} onClick={nextImage}>
          <FaChevronRight />
        </button>
      </div>
      <div className={styles.thumbnailSlider}>
        <button 
          className={styles.thumbnailNavButton}
          onClick={() => scrollThumbnails('left')}
        >
          <FaChevronLeft />
        </button>
        <div className={styles.thumbnailContainer} ref={thumbnailContainerRef}>
          {images.map((image, index) => (
            <img
              key={image.id}
              src={image.fullUrl}
              alt={image.name}
              className={`${styles.thumbnail} ${index === currentIndex ? styles.active : ''}`}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </div>
        <button 
          className={styles.thumbnailNavButton}
          onClick={() => scrollThumbnails('right')}
        >
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}; 